/**
 * ListDesktopRow Component
 *
 * A reusable desktop row component for list views with consistent column layout.
 * Designed to match ListHeader column definitions.
 *
 * @example
 * ```tsx
 * <ListDesktopRow
 *   columns={[
 *     { key: 'date', width: 'w-28', content: <span>Jan 1, 2024</span> },
 *     { key: 'name', width: 'flex-1', content: <span>John Doe</span> },
 *     { key: 'status', width: 'w-24', align: 'center', content: <Badge>Active</Badge> },
 *   ]}
 *   actions={[
 *     { icon: <Edit size={14} />, label: 'Edit', onClick: handleEdit },
 *   ]}
 * />
 * ```
 */
import React from 'react';
import { ColumnAlignment } from './ListHeader';

export interface DesktopRowColumn {
  /** Unique identifier matching ListHeaderColumn.key */
  key: string;
  /** CSS width class (should match ListHeaderColumn.width) */
  width?: string;
  /** Text alignment (should match ListHeaderColumn.align) */
  align?: ColumnAlignment;
  /** Content to render in this cell */
  content: React.ReactNode;
}

export interface DesktopRowAction {
  /** Icon to display */
  icon: React.ReactNode;
  /** Action label (shown as tooltip) */
  label: string;
  /** Click handler */
  onClick: () => void;
  /** Optional CSS class */
  className?: string;
  /** Whether the action is visible */
  isVisible?: boolean;
  /** Whether the action is disabled */
  disabled?: boolean;
}

export interface ListDesktopRowProps {
  /** Array of column content */
  columns: DesktopRowColumn[];
  /** Array of action buttons */
  actions?: DesktopRowAction[];
  /** Width class for the actions column (should match ListHeader.actionsWidth) */
  actionsWidth?: string;
  /** Optional CSS class for the container */
  className?: string;
}

const getAlignmentClass = (align: ColumnAlignment = 'left'): string => {
  const alignments: Record<ColumnAlignment, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };
  return alignments[align];
};

const ListDesktopRow: React.FC<ListDesktopRowProps> = ({
  columns,
  actions = [],
  actionsWidth = 'w-20',
  className = '',
}) => {
  // Filter to only visible actions
  const visibleActions = actions.filter((action) => action.isVisible !== false);

  return (
    <div className={`px-4 py-3 flex items-center justify-between ${className}`}>
      {columns.map((column) => (
        <div
          key={column.key}
          className={`${column.width || 'flex-1 min-w-0'} ${getAlignmentClass(column.align)}`}
        >
          {column.content}
        </div>
      ))}
      {visibleActions.length > 0 && (
        <div className={`${actionsWidth} flex justify-center space-x-1`}>
          {visibleActions.map((action, index) => (
            <button
              key={index}
              className={
                action.className ||
                'p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              }
              onClick={action.onClick}
              disabled={action.disabled}
              title={action.label}
              aria-label={action.label}
            >
              {action.icon}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ListDesktopRow;
